<template>
  <input
    :class="props.class"
    v-model="value"
    :type="props.type"
    :placeholder="props.placeholder"
    :name="props.name"
  />
  <p v-if="displayErrorMessage" class="p-inputerror__error">
    {{ displayErrorMessage }}
  </p>
</template>

<script lang="ts">
import { defineComponent, watch, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useField } from 'vee-validate'

export default defineComponent({
  name: 'InputText',
  props: {
    /**
     * v-modelの値
     * 設定必須
     */
    inputValue: {
      type: String,
      default: '',
      required: true,
    },
    /**
     * <input> typeの指定
     */
    type: {
      type: String,
      default: 'text',
    },
    /**
     * 表示するplaceholder
     */
    placeholder: {
      type: String,
      default: '',
    },
    /**
     * バリデーションを行う対象のtext-fieldにつける名称
     * 設定必須... vee-validateで使用するため
     */
    name: {
      type: String,
      default: '',
      required: true,
    },
    /**
     * 下記で定義されているルールを文字列として渡すことでバリデーションがかけられる。
     * src/validate/VeeValidateConfigs.ts
     * ex.) "required|passwordFormat" ← 必須かつパスワードの正規表現でチェックする
     */
    rules: {
      type: String,
      default: '',
    },
    /**
     * <input type="text"/>のスタイルを当てる際に使用
     */
    class: {
      type: String,
      default: '',
    },
    /**
     * APIによる重複チェックなどこのコンポーネント内では完結しないエラーがあった場合に、
     * 外部から引き渡すことでコンポーネント内のメッセージ表示エリアに表示可能
     */
    otherErrorMessage: {
      type: Array,
      default: null,
    },
    /**
     * 入力が停止したと思われるタイミングで@delay-inputを発火するための遅延時間。
     * 遅延を調整したい場合に値を渡す。(ms)
     */
    inputDelay: {
      type: Number,
      default: 500,
    },
  },
  emits: ['update:inputValue', 'input', 'delayinput'],
  setup(props, context) {
    /**
     * バリデーションのルールを動的に変更するための値
     */
    const rules = computed(() => {
      return props.rules
    })
    /**
     * vee-validateのオブジェクトを返す
     * @see https://vee-validate.logaretm.com/v4/api/use-field
     */
    const { value, errorMessage, validate, meta } = computed(() => {
      return useField(props.name, rules, {
        initialValue: props.inputValue,
      })
    }).value
    // エラーメッセージの動的なマルチ言語対応
    const { locale } = useI18n()
    watch(locale, () => {
      validate()
    })
    // delay-inputを発火させるTimeout
    let inputTimeout: number
    /**
     * 入力後、指定された遅延時間を過ぎたら入力した値を返すイベントを発火する
     * @param inputValue 入力した文字列
     */
    const eventInputWithDelay = async (inputValue: string) => {
      if (inputTimeout) clearTimeout(inputTimeout)
      inputTimeout = setTimeout(() => context.emit('delayinput', inputValue), props.inputDelay)
    }
    /**
     * 入力値に変更があったら実行する。
     */
    watch(value, () => {
      context.emit('input', value)
      eventInputWithDelay(value)
      context.emit('update:inputValue', value)
    })
    /**
     * 表示するエラーメッセージ
     */
    const displayErrorMessage = computed(() => {
      if (errorMessage.value) return errorMessage.value
      if (props.otherErrorMessage && props.otherErrorMessage.length > 0)
        return props.otherErrorMessage[0]
      return ''
    })
    onMounted(() => {
      // 値が設定されている場合(画面遷移で戻った時など)にはvalidateする
      if (value.value) validate()
    })
    return {
      props,
      value,
      errorMessage,
      displayErrorMessage,
      meta,
      eventInputWithDelay,
      validate,
    }
  },
})
</script>

<style scoped lang="scss"></style>
